import * as React from "react";
import { entryForId, LAYERS, type Layer } from "./registry";

/**
 * Hash router for the playground.
 *
 * One component per view, rather than all 118 demos stacked on a single page.
 * The old single-scroll surface rendered a ~131,000px document on every load —
 * every chart, canvas animation, portal and observer in the library mounting at
 * once — which made it slow to open and impossible to read a component against
 * anything but its neighbours.
 *
 * The URL grammar is deliberately the one the playground already published, so
 * every deep link pasted into a design-QA issue keeps resolving:
 *
 *   #button          → the Button page      (unchanged from the old anchors)
 *   #/button         → the same page        (tolerated, canonicalized to #button)
 *   #layer-primitive → the Primitives index (unchanged from the old anchors)
 *   #/ or empty      → the overview
 *
 * An unrecognized hash falls back to the overview rather than rendering an
 * error page — a stale link should land somewhere useful.
 */

export type Route =
  | { kind: "overview" }
  | { kind: "layer"; id: Layer }
  | { kind: "component"; id: string };

export const HREF_OVERVIEW = "#/";

/** Canonical href for a component page. */
export function hrefForComponent(id: string): string {
  return `#${id}`;
}

/** Canonical href for a layer index page. */
export function hrefForLayer(id: Layer): string {
  return `#layer-${id}`;
}

const LAYER_IDS = new Set<string>(LAYERS.map((l) => l.id));

function isLayer(value: string): value is Layer {
  return LAYER_IDS.has(value);
}

export function parseRoute(hash: string): Route {
  // Strips the leading "#" and an optional "/", so `#button` and `#/button`
  // both resolve — the second form is what a copied address bar tends to
  // produce once a router is involved.
  const raw = decodeURIComponent(hash.replace(/^#\/?/, "")).trim();
  if (!raw) return { kind: "overview" };

  const layer = /^layer-(.+)$/.exec(raw);
  if (layer && isLayer(layer[1])) return { kind: "layer", id: layer[1] };

  if (entryForId(raw)) return { kind: "component", id: raw };

  // A bare layer id (`#stock`) — not a link the app produces, but the obvious
  // thing to type, and it resolves unambiguously: no component shares a name
  // with a layer. Checked after components so a component would always win.
  if (isLayer(raw)) return { kind: "layer", id: raw };

  return { kind: "overview" };
}

/** The route for the current `location.hash`, re-read on every hash change. */
export function useRoute(): Route {
  const [route, setRoute] = React.useState<Route>(() =>
    parseRoute(typeof window === "undefined" ? "" : window.location.hash),
  );

  React.useEffect(() => {
    const onHashChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    // Re-sync on mount: the hash may have changed between the initial
    // useState call and this effect (an in-page anchor, a restored session).
    onHashChange();
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  return route;
}

/** Programmatic navigation — used by the command palette and the pager. */
export function navigate(href: string): void {
  if (window.location.hash === href) return;
  window.location.hash = href;
}

/** Stable key for a route, for `React.key` and effect dependencies. */
export function routeKey(route: Route): string {
  return route.kind === "overview" ? "overview" : `${route.kind}:${route.id}`;
}
